import { ProtectedRoute } from '@/components/protected-route';
import { PATH } from '@repo/constants';
import { UserPermissionMap } from '@repo/types';
import { DashboardLayout, MainSuspense, RouterSuspense } from '@ui/components';
import { ErrorPage } from '@ui/pages';
import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

const DashboardPage = lazy(() => import('../pages/dashboard'));
const AuthorizationPage = lazy(() => import('@ui/pages/authorization'));
const StatementPage = lazy(() => import('../pages/reports/statement'));
const PortfolioSummaryPage = lazy(
  () => import('../pages/reports/portfolio-summary'),
);
const TransactionPage = lazy(() => import('../pages/reports/transaction'));
const DailyExposurePage = lazy(() => import('../pages/reports/daily-exposure'));
const CommissionRebatePage = lazy(
  () => import('../pages/reports/commission-rebate'),
);
const SalesTradingPage = lazy(() => import('../pages/reports/sales-trading'));
const TurnoverByRegionPage = lazy(
  () => import('../pages/reports/turnover-by-region'),
);
const ACGMarkupReportPage = lazy(() => import('../pages/reports/acg-markup'));
const AccountSummaryPage = lazy(
  () => import('../pages/reports/account-summary'),
);
const OpenPositionsPage = lazy(
  () => import('../pages/positions/open-positions'),
);
const TradeBlotterPage = lazy(() => import('../pages/positions/trade-blotter'));
const ClientsPage = lazy(
  () => import('../pages/configuration/client/client-page'),
);
const ClientDetailPage = lazy(
  () => import('../pages/configuration/client-details'),
);
const MarketsPage = lazy(() => import('../pages/configuration/markets'));
const RegionsPage = lazy(() => import('../pages/configuration/regions'));
const BrokersPages = lazy(
  () => import('../pages/configuration/client/broker-page'),
);
const TradersPage = lazy(() => import('../pages/configuration/traders'));
const ExchangePages = lazy(() => import('../pages/configuration/exchange'));
const ClientUnitSizePage = lazy(
  () => import('../pages/configuration/client-unit-size'),
);
const CustodyCashPage = lazy(
  () => import('../pages/custody-data/custody-cash'),
);
const CustodyPositionsPage = lazy(
  () => import('../pages/custody-data/custody-positions'),
);
const CustodyTransactionsPage = lazy(
  () => import('../pages/custody-data/custody-transactions'),
);
const SecurityDictionaryPage = lazy(
  () => import('../pages/configuration/securities-dictionary'),
);
const ClientRootPage = lazy(
  () => import('../pages/configuration/client-root/client-root-page'),
);
const BrokerRootPage = lazy(
  () => import('../pages/configuration/client-root/broker-root-page'),
);
const PairTradingPage = lazy(() => import('../pages/strategy/pair-trading'));
const SearchTradePage = lazy(() => import('../pages/trades/trade-search'));
const TradeEntryPage = lazy(() => import('../pages/trades/trade-entry'));
const CorporateActionPage = lazy(
  () => import('../pages/trades/corporate-actions'),
);
const TradeConfirmationPage = lazy(
  () => import('../pages/trades/confirmation'),
);
const TradeAllocationPage = lazy(() => import('../pages/trades/allocation'));
const MarginProcessingPage = lazy(
  () => import('../pages/trades/margin-processing'),
);
const SettlementHolidayPage = lazy(
  () => import('../pages/configuration/settlement-holiday'),
);
const FeesBookingPage = lazy(() => import('../pages/trades/fees-booking'));
const RecalculationPage = lazy(() => import('../pages/tools/recalculation'));
const ClientGroupsPage = lazy(
  () => import('../pages/configuration/client-groups'),
);
const RebalancePage = lazy(() => import('../pages/tools/rebalance'));
const ReconciliationPage = lazy(
  () => import('../pages/reconciliation/reconciliation'),
);
const ImportFilesPage = lazy(
  () => import('../pages/file-management/import-files'),
);
const CustodyImportFilesPage = lazy(
  () => import('../pages/file-management/custody-import-files'),
);
const CreateMIFIDPage = lazy(
  () => import('../pages/regulatory-reporting/create-mifid'),
);
const ProductPricingPage = lazy(() => import('../pages/configuration/pricing'));
const CounterpartPositionPage = lazy(
  () => import('../pages/reconciliation/counterparty-positions'),
);
const UploadedFilesPage = lazy(
  () => import('../pages/file-management/uploaded-files'),
);
const DocumentationPage = lazy(() => import('../pages/tools/documentation'));
const CheckQuantitiesPage = lazy(
  () => import('../pages/regulatory-reporting/check-quantities'),
);
const VirtuPage = lazy(() => import('../pages/counterparties/virtu'));
const GoldmanSachsPage = lazy(
  () => import('../pages/counterparties/goldman-sachs'),
);
const MaintenancePage = lazy(() => import('@ui/pages/maintenance'));
const ExternalContactsPage = lazy(
  () => import('../pages/tools/external-contact'),
);
const TradersPlPage = lazy(() => import('../pages/reports/traders-pl'));
const TimelinePage = lazy(() => import('../pages/reports/timeline'));
const FactsheetPage = lazy(() => import('../pages/reports/factsheet'));
const PatternRecognitionPage = lazy(
  () => import('../pages/technical-indicators/rsi-pattern-recognition'),
);
const RsiHistoryPage = lazy(
  () => import('../pages/technical-indicators/rsi-history'),
);
const PortfoliosPage = lazy(() => import('../pages/reports/portfolios'));
const PortfolioDetailsPage = lazy(
  () => import('../pages/reports/portfolios-details'),
);

export const getRouter = (permissions?: UserPermissionMap) => {
  const hasCustodyAccess =
    !!permissions?.view_custody_data ||
    !!permissions?.view_configuration_client_unit_size;

  return createBrowserRouter([
    {
      path: PATH.DASHBOARD,
      element: (
        <MainSuspense>
          <DashboardLayout />
        </MainSuspense>
      ),
      ErrorBoundary: ErrorPage,
      children: [
        {
          path: '',
          index: true,
          element: (
            <RouterSuspense>
              <DashboardPage />
            </RouterSuspense>
          ),
        },
        {
          path: PATH.REPORT_FACTSHEET,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_report_factsheet}>
              <FactsheetPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REPORT_STATEMENT,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_report_statement}>
              <StatementPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REPORT_PORTFOLIO_SUMMARY,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_report_portfolio_summary}
            >
              <PortfolioSummaryPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REPORT_TRANSACTION,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_report_transaction}>
              <TransactionPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REPORT_DAILY_EXPOSURE,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_report_daily_exposure}
            >
              <DailyExposurePage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REPORT_COMMISSION_REBATE,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_report_commission_rebate}
            >
              <CommissionRebatePage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REPORT_SALES_TRADING,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_report_sales_trading}
            >
              <SalesTradingPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REPORT_TURNOVER_BY_REGION,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_report_turnover_by_region}
            >
              <TurnoverByRegionPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.ACGMarkup,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_report_acg_markup}>
              <ACGMarkupReportPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.ACCOUNT_SUMMARY,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_report_accounts}>
              <AccountSummaryPage
                isAdmin={!!permissions?.edit_report_accounts}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.OPEN_POSITION,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_positions_open_positions}
            >
              <OpenPositionsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.TRADE_BLOTTER,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_positions_trade_blotter}
            >
              <TradeBlotterPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REPORT_PORTFOLIOS,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_portfolios}>
              <PortfoliosPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REPORT_PORTFOLIO_DETAILS + '/:id',
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_portfolios}>
              <PortfolioDetailsPage
                canViewPerformance={!!permissions?.view_portfolios_performance}
                canViewPositions={!!permissions?.view_portfolios_positions}
                canViewSummary={!!permissions?.view_portfolios_summary}
                canViewTransactions={!!permissions?.view_portfolios_transactions}
              />
            </ProtectedRoute>
          ),
        },

        {
          path: PATH.CLIENT,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_clients}
            >
              <ClientsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CLIENT_DETAIL + '/:id',
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_clients}
            >
              <ClientDetailPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.BROKER_DETAIL + '/:id',
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_brokers}
            >
              <ClientDetailPage />
            </ProtectedRoute>
          ),
        },

        {
          path: PATH.MARKETS,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_markets}
            >
              <MarketsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REGIONS,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_regions}
            >
              <RegionsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.BROKERS,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_brokers}
            >
              <BrokersPages />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.TRADERS,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_traders}
            >
              <TradersPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.EXCHANGE,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_exchanges}
            >
              <ExchangePages />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CLIENT_UNIT_SIZE,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_client_unit_size}
            >
              <ClientUnitSizePage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CUSTODY_DATA,
          element: (
            <ProtectedRoute isAllowed={hasCustodyAccess}>
              <Navigate
                to={PATH.CUSTODY_DATA_CASH}
                replace
              />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CUSTODY_DATA_CONFIGURATION,
          element: (
            <ProtectedRoute isAllowed={hasCustodyAccess}>
              <Navigate
                to={PATH.CUSTODY_DATA_CASH}
                replace
              />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CUSTODY_DATA_CONFIGURATION + '/:id',
          element: (
            <ProtectedRoute isAllowed={hasCustodyAccess}>
              <Navigate
                to={PATH.CUSTODY_DATA_CASH}
                replace
              />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CUSTODY_DATA_CASH,
          element: (
            <ProtectedRoute isAllowed={hasCustodyAccess}>
              <CustodyCashPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CUSTODY_DATA_TRANSACTIONS,
          element: (
            <ProtectedRoute isAllowed={hasCustodyAccess}>
              <CustodyTransactionsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CUSTODY_DATA_POSITIONS,
          element: (
            <ProtectedRoute isAllowed={hasCustodyAccess}>
              <CustodyPositionsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.SECURITIES_DICTIONARY,
          element: (
            <ProtectedRoute
              isAllowed={
                !!permissions?.view_configuration_securities_dictionary
              }
            >
              <SecurityDictionaryPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CREATE_CLIENT,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_client_root}
            >
              <ClientRootPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CREATE_BROKER,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_broker_root}
            >
              <BrokerRootPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.STRATEGY_PAIR,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_strategy_pair_trading}
            >
              <PairTradingPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.TRADE_SEARCH,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_trade_search_trade}>
              <SearchTradePage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.TRADE_DETAIL + '/:id',
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_trade_trade_entry}>
              <TradeEntryPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CORPORATE_ACTIONS,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_trade_corporate_actions}
            >
              <CorporateActionPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.TRADE_CONFIRMATION,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_trade_trade_confirmation}
            >
              <TradeConfirmationPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.TRADE_ALLOCATION,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_trade_trade_allocation}
            >
              <TradeAllocationPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.MARGIN_PROCESSING,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_trade_margin_processing}
            >
              <MarginProcessingPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.SETTLEMENT_HOLIDAY,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_tools_settlement_holiday}
            >
              <SettlementHolidayPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.FEES_BOOKING,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_trade_fees_booking}>
              <FeesBookingPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CLIENT_GROUPS,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_client_groups}
            >
              <ClientGroupsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.RECALCULATION,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_tools_recalculation}>
              <RecalculationPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REBALANCE,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_tools_rebalance}>
              <RebalancePage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.RECONCILIATION,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_reconciliation_reconciliation}
            >
              <ReconciliationPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.IMPORT_FILE,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_file_management_import_files}
            >
              <ImportFilesPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CUSTODY_IMPORT_FILE,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_file_management_import_files}
            >
              <CustodyImportFilesPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.MIFID,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_regulatory_reporting_create_mifid}
            >
              <CreateMIFIDPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.PRICING,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_configuration_product_pricing}
            >
              <ProductPricingPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.COUNTERPARTY_POSITIONS,
          element: (
            <ProtectedRoute
              isAllowed={
                !!permissions?.view_reconciliation_counterparty_positions
              }
            >
              <CounterpartPositionPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.UPLOADED_FILES,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_file_management_uploaded_files}
            >
              <UploadedFilesPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.DOCUMENTATION + '/:id?',
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_documentation_readonly}
            >
              <DocumentationPage
                isAdmin={!!permissions?.view_documentation_editable}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.CHECK_QUANTITIES,
          element: (
            <ProtectedRoute
              isAllowed={
                !!permissions?.view_regulatory_reporting_check_quantities
              }
            >
              <CheckQuantitiesPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.VIRTU,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_counterparties_virtu}
            >
              <VirtuPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.GOLDMAN_SACHS,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_counterparties_goldman_sachs}
            >
              <GoldmanSachsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.EXTERNAL_CONTACTS,
          element: (
            <ProtectedRoute
              isAllowed={!!permissions?.view_tools_external_contacts}
            >
              <ExternalContactsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.TRADERS_PL,
          element: (
            <ProtectedRoute
              isAllowed={
                !!permissions?.view_report_traders_pl_own ||
                !!permissions?.view_report_traders_pl_all
              }
            >
              <TradersPlPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.REPORT_TIMELINE,
          element: (
            <ProtectedRoute isAllowed={!!permissions?.view_report_timeline}>
              <TimelinePage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.TECHNICAL_INDICATORS_RSI_PATTERN_RECOGNITION,
          element: (
            <ProtectedRoute
              isAllowed={
                !!permissions?.view_technical_indicators_rsi_pattern_recognition
              }
            >
              <PatternRecognitionPage />
            </ProtectedRoute>
          ),
        },
        {
          path: PATH.TECHNICAL_INDICATORS_RSI_HISTORY,
          element: (
            <ProtectedRoute
              isAllowed={
                !!permissions?.view_technical_indicators_rsi_pattern_recognition
              }
            >
              <RsiHistoryPage />
            </ProtectedRoute>
          ),
        },
      ],
    },
    {
      path: PATH.AUTH_LOGIN,
      element: (
        <MainSuspense>
          <AuthorizationPage />
        </MainSuspense>
      ),
    },
    {
      path: PATH.MAINTENANCE + '/:type',
      element: (
        <RouterSuspense>
          <MaintenancePage />
        </RouterSuspense>
      ),
    },
    {
      path: '*',
      element: <Navigate to={PATH.DASHBOARD} />,
    },
  ]);
};
